import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

// Route-level error screen (react-router errorElement). Without one, any render
// error unmounts the app into react-router's bare default page. Attached to
// every page route (so the Layout nav survives a page crash) and to the two
// top-level routes as a full-screen catch-all. The Google-Translate-induced
// removeChild crash is prevented earlier by utils/translateGuard.js; this
// screen covers whatever still throws.
export default function RouteErrorBoundary({ fullScreen = false }) {
  const error = useRouteError();
  const navigate = useNavigate();
  const message =
    error instanceof Error
      ? error.message
      : error?.statusText || String(error ?? 'Unknown error');

  return (
    <div
      className={`flex items-center justify-center p-6 ${
        fullScreen ? 'min-h-screen bg-page-bg' : 'min-h-[60vh]'
      }`}
    >
      <div className="max-w-md w-full rounded-[10px] border border-card-border bg-card-bg py-12 px-8 text-center">
        <AlertTriangle size={48} strokeWidth={2} className="mx-auto mb-6 text-page-muted" />
        <h1 className="text-2xl font-semibold text-page-text mb-2">Something went wrong</h1>
        <p className="text-sm text-page-muted mb-6 break-words">{message}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            Go Back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-hover transition-colors"
          >
            <RefreshCw size={20} strokeWidth={2.5} />
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
